import { Link } from '@tanstack/react-router';
import { EmptyState, buttonVariants } from '@wundo/ui';
import { AddMeetingButton } from '@/components/agenda/AddMeetingButton.tsx';
import { m } from '@/paraglide/messages.js';

export interface NoCalendarEmptyStateProps {
  userId: string;
  /* What is not there yet, in this page's own words. */
  title: string;
  description: string;
  className?: string;
}

/**
 * The empty page of somebody whose calendar Wundo cannot see.
 *
 * Not every company lets the calendar permission through, so a page that only
 * says "koppel je agenda" is a dead end for the people who are never allowed
 * to. It offers both ways out instead, side by side: write a conversation down
 * by hand, or hand over the calendar. Neither is a step in a wizard — the page
 * fills itself the moment either one is done.
 *
 * The dialog behind the first button is the real one, not a link to the page
 * that has it: sending somebody to the agenda to find a button is a detour, and
 * two copies of the form would drift.
 *
 * Agenda, team and gesprekken all show this, because all three are empty for
 * the same reason and the way out of it is the same on each.
 */
export function NoCalendarEmptyState({
  userId,
  title,
  description,
  className,
}: NoCalendarEmptyStateProps) {
  return (
    <EmptyState
      className={className}
      title={title}
      description={description}
      action={
        <div className='flex flex-wrap items-center justify-center gap-2'>
          <AddMeetingButton userId={userId} />
          <Link
            to='/settings/connections'
            className={buttonVariants({ variant: 'quiet', size: 'sm' })}
          >
            {m.empty_connect_calendar()}
          </Link>
        </div>
      }
    />
  );
}
